/**
 * useProposalKanbanColumn - Carrega as propostas de uma coluna do Kanban de Propostas
 *
 * Cursor-based pagination (updated_at + id) por coluna, espelhando
 * useContractKanbanColumn.ts. Cada coluna carrega apenas a própria página.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ProposalStatus } from '@/hooks/useDealProposals';

// Logger centralizado (padrão do projeto)
const logger = createLogger('ProposalKanbanColumn');

/** Tamanho da página por coluna */
export const PROPOSAL_KANBAN_PAGE_SIZE = 5; // v1.1.0: carga inicial reduzida (20 -> 5)

/** Filtros compartilhados entre Kanban e (futura) listagem de propostas */
export interface ProposalFilters {
  search?: string | null;
  workspace_id?: string | null;
  seller_id?: string | null;
  assigned_to?: string | null;
  proposal_type_id?: string | null;
  pipeline_id?: string | null;
  phase_id?: string | null;
  deal_id?: string | null;
  created_by?: string | null;
  date_field?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  min_total?: number | null;
  max_total?: number | null;
  min_mrr?: number | null;
  max_mrr?: number | null;
  pdf_status?: string | null;
  public_enabled?: boolean | null;
  viewed?: boolean | null;
  tag_ids?: string[] | null;
  /** Usado apenas na listagem (no Kanban o status é a coluna) */
  status?: ProposalStatus | null;
}

/** Proposta exibida no card do Kanban (retorno da RPC) */
export interface KanbanProposal {
  id: string;
  deal_id: string | null;
  proposal_number: string;
  version_number: number;
  title: string | null;
  status: ProposalStatus;
  currency: string;
  subtotal: number;
  discount_total: number;
  total: number;
  mrr_total: number;
  items_count: number;
  valid_from: string | null;
  expires_at: string | null;
  is_expired: boolean;
  pdf_status: string | null;
  pdf_storage_path: string | null;
  public_enabled: boolean;
  public_token: string | null;
  sent_at: string | null;
  first_viewed_at: string | null;
  accepted_at: string | null;
  rejected_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  proposal_type_id: string | null;
  proposal_type_name: string | null;
  proposal_type_color: string | null;
  deal_title: string | null;
  deal_value: number | null;
  deal_currency: string | null;
  contact_name: string | null;
  contact_avatar: string | null;
  seller_name: string | null;
  seller_avatar: string | null;
  phase_name: string | null;
  workspace_name: string | null;
}

/** Cursor de paginação por coluna */
interface ProposalKanbanCursor {
  updated_at: string;
  id: string;
}

/** Converte os filtros do frontend nos parâmetros da RPC */
export function buildProposalRpcParams(filters?: ProposalFilters) {
  return {
    p_workspace_id: filters?.workspace_id || null,
    p_search: filters?.search || null,
    p_seller_id: filters?.seller_id || null,
    p_assigned_to: filters?.assigned_to || null,
    p_proposal_type_id: filters?.proposal_type_id || null,
    p_pipeline_id: filters?.pipeline_id || null,
    p_phase_id: filters?.phase_id || null,
    p_deal_id: filters?.deal_id || null,
    p_created_by: filters?.created_by || null,
    p_date_field: filters?.date_field || null,
    p_date_from: filters?.date_from || null,
    p_date_to: filters?.date_to || null,
    p_min_total: filters?.min_total ?? null,
    p_max_total: filters?.max_total ?? null,
    p_min_mrr: filters?.min_mrr ?? null,
    p_max_mrr: filters?.max_mrr ?? null,
    p_pdf_status: filters?.pdf_status || null,
    p_public_enabled: filters?.public_enabled ?? null,
    p_viewed: filters?.viewed ?? null,
    p_tag_ids: filters?.tag_ids?.length ? filters.tag_ids : null,
  };
}

/**
 * Hook para carregar uma coluna (situação) do Kanban de Propostas.
 */
export function useProposalKanbanColumn(
  status: ProposalStatus,
  filters?: ProposalFilters,
  enabled: boolean = true,
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['proposal-kanban-column', currentTenant?.id, status, filters],
    queryFn: async ({ pageParam }) => {
      logger.debug(`Buscando propostas da coluna ${status}, cursor:`, pageParam);

      const { data, error } = await supabase.rpc('get_proposals_kanban_column', {
        p_status: status,
        p_limit: PROPOSAL_KANBAN_PAGE_SIZE,
        p_cursor_updated_at: pageParam?.updated_at || null,
        p_cursor_id: pageParam?.id || null,
        ...buildProposalRpcParams(filters),
        p_tenant_hint: currentTenant?.id || null,
      } as any);

      if (error) {
        logger.error('Erro ao buscar coluna do Kanban de Propostas:', error.message);
        throw error;
      }

      return (data || []) as unknown as KanbanProposal[];
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PROPOSAL_KANBAN_PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return { updated_at: lastItem.updated_at, id: lastItem.id } as ProposalKanbanCursor;
    },
    initialPageParam: null as ProposalKanbanCursor | null,
    enabled: !!currentTenant?.id && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    // Rede de segurança — o Realtime é a fonte principal de atualização
    refetchInterval: 60_000,
  });

  const proposals = query.data?.pages.flat() || [];

  return {
    proposals,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
  };
}

/**
 * Invalida todas as colunas + contagens do Kanban de Propostas.
 */
export function useInvalidateProposalKanban() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ['proposal-kanban-column'] });
    queryClient.invalidateQueries({ queryKey: ['proposal-kanban-counts'] });
  };
}
